import type { Notification, NotificationListMeta, NotificationPrefs } from '@proref/types';
import type { UpdateNotificationPrefsInput } from '@proref/schemas';
import { api, unwrap, type Envelope } from '../lib/api';

// The bell's data access. GET /v1/notifications returns the page in `data` and the unread count +
// keyset cursor in `meta`, so — unlike every other service here — these calls can't just `unwrap`:
// the badge lives in the envelope's meta, not in the payload.
export interface NotificationPage {
  items: Notification[];
  meta: NotificationListMeta;
}

export interface ListNotificationsParams {
  limit?: number;
  // Opaque "<created_at>|<id>" cursor from a previous page's meta.next_cursor.
  cursor?: string | null;
  unreadOnly?: boolean;
}

// A page of the caller's own notifications, newest first.
export async function listNotifications(
  params: ListNotificationsParams = {},
): Promise<NotificationPage> {
  const res = await api.get<Envelope<Notification[]>>('/notifications', {
    params: {
      ...(params.limit ? { limit: params.limit } : {}),
      ...(params.cursor ? { cursor: params.cursor } : {}),
      // The API takes the literal strings 'true'/'false', not a coerced boolean — see
      // listNotificationsQuerySchema. Omitted entirely when we don't care.
      ...(params.unreadOnly ? { unread_only: 'true' } : {}),
    },
  });
  const meta = (res.data.meta ?? {}) as Partial<NotificationListMeta>;
  return {
    items: res.data.data ?? [],
    // Defaulted rather than trusted: a proxy or an older API build that drops meta should degrade
    // to "no badge, no next page" instead of rendering NaN or looping on an undefined cursor.
    meta: {
      unread_count: typeof meta.unread_count === 'number' ? meta.unread_count : 0,
      next_cursor: typeof meta.next_cursor === 'string' ? meta.next_cursor : null,
    },
  };
}

// Mark a specific set read, or the whole inbox. The API takes exactly one of the two.
export async function markNotificationsRead(input: { ids: string[] } | { all: true }): Promise<void> {
  await api.post<Envelope<null>>('/notifications/read', input);
}

// The caller's notification preferences, always RESOLVED — the API merges what they have actually
// saved over the code-side defaults, so the settings form renders from this without knowing which
// keys were persisted.
export async function getNotificationPrefs(): Promise<NotificationPrefs> {
  const res = await api.get<Envelope<NotificationPrefs>>('/me/notification-prefs');
  return unwrap(res.data);
}

// Apply a PARTIAL patch — send only the category that changed. The API merges per channel, so
// sending the whole resolved set would persist today's defaults into the user's row and stop
// untouched categories from tracking them. Returns the new resolved set.
export async function updateNotificationPrefs(
  patch: UpdateNotificationPrefsInput,
): Promise<NotificationPrefs> {
  const res = await api.patch<Envelope<NotificationPrefs>>('/me/notification-prefs', patch);
  return unwrap(res.data);
}
